// pages/ServicingDashboard/FinanceDashboard.jsx - REFACTORED WITH RESPONSIVE DESIGN
import { useToast } from "../../hooks/useToast";
import { ToastContainer } from "../../components/common/Toast";

import { useState, useEffect, useMemo, useCallback, memo } from "react";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import { useRevenueChart } from "../../hooks/finance/useRevenueChart";
import { useCustomerBreakdown } from "../../hooks/finance/useCustomerBreakdown";
import { useFinancialHealth } from "../../hooks/finance/useFinancialHealth";
import { useAMIBreakdown } from "../../hooks/finance/useAMIBreakdown";
import { useCashRunway } from "../../hooks/finance/useCashRunway";
import { useAccountsReceivable } from "../../hooks/finance/useAccountsReceivable";
import { useCustomerMetrics } from "../../hooks/finance/useCustomerMetrics";
import { useMaxDates } from "../../hooks/finance/useMaxDates";
import { Header } from "../../components/finance/Header";
import { RevenueGoal } from "../../components/finance/sections/RevenueGoal";
import { RevenueDashboard } from "../../components/finance/sections/RevenueDashboard";
import { CashRunway } from "../../components/finance/sections/CashRunway";
import { AccountsReceivable } from "../../components/finance/sections/AccountReceivables";
import { FinancialHealth } from "../../components/finance/sections/FinancialHealth";
import { ClientMix } from "../../components/finance/sections/ClientMix";
import { AMIBreakdown } from "../../components/finance/sections/AMIBreakdown";
import CollapsibleSection from "../../components/common/CollapsibleSection";
import { LoadingSpinner } from "../../components/common";
import { ErrorMessage } from "../../components/common";
import { NoDataMessage } from "../../components/common";


import { 
  getDefaultDateRange, 
  getCurrentCycleMonth,
  setDocumentMeta, 
  refreshDashboardCache 
} from '../../utils/dashboardUtils';
import { 
  DEFAULT_SECTION_STATE, 
  CACHE_ENDPOINTS, 
  DOCUMENT_TITLES 
} from '../../utils/dashboardConstants';

// Refresh button component for section headers
const RefreshButton = memo(({ onClick, refreshing }) => (
  <button
    onClick={onClick}
    disabled={refreshing}
    className="p-1.5 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
    title="Refresh this section"
  >
    <ArrowPathIcon
      className={`h-5 w-5 text-gray-600 ${refreshing ? "animate-spin" : ""}`}
    />
  </button>
));

const FinanceDashboard = () => {
  
  const { toasts, removeToast} = useToast();

  // Finance data hook
  const {
    chartData,
    summaryStats,
    yoyComparison,
    loading,
    refreshing: refreshingRevenueChart,
    error,
    initialized,
    lastUpdated,
    dateRange,
    setDateRange,
    refetch: refetchRevenueChart,
  } = useRevenueChart();

  // Max dates hook for date range formatting - CONSISTENTLY USE maxDates
  const { maxDates } = useMaxDates();

  // SINGLE SOURCE OF TRUTH: Calculate YTD date range
  const ytdDateRange = useMemo(() => {
    if (!dateRange?.start_date) {
      const currentYear = new Date().getFullYear();
      return {
        start_date: `${currentYear}-01-01`,
        end_date: maxDates?.maxDateUpdated || new Date().toISOString().split('T')[0]
      };
    }
    
    // Parse year directly from string to avoid timezone issues
    const selectedYear = parseInt(dateRange.start_date.split('-')[0], 10);
    const currentYear = new Date().getFullYear();
    
    const startDate = `${selectedYear}-01-01`;
    
    let endDate;
    if (selectedYear === currentYear && maxDates?.maxDateUpdated) {
      // For current year: use the max transaction date from API
      endDate = maxDates.maxDateUpdated;
    } else if (selectedYear === currentYear) {
      // Fallback to today if API data not available
      endDate = new Date().toISOString().split('T')[0];
    } else {
      // For past years: show full year
      endDate = `${selectedYear}-12-31`;
    }
    
    return { start_date: startDate, end_date: endDate };
  }, [dateRange, maxDates]);

  // Format date range for display using ytdDateRange
  const formattedDateRange = useMemo(() => {
    if (!ytdDateRange?.start_date || !ytdDateRange?.end_date) {
      return null;
    }
    
    const formatDate = (dateStr) => {
      const [year, month, day] = dateStr.split('-').map(num => parseInt(num, 10));
      const date = new Date(year, month - 1, day); // month is 0-indexed
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    };
    
    return `${formatDate(ytdDateRange.start_date)} - ${formatDate(ytdDateRange.end_date)}`;
  }, [ytdDateRange]);


  // Customer breakdown hook
  const {
    customerBreakdown,
    totalCustomers,
    totalRetainers,
    totalProjects,
    loading: loadingCustomers,
    refreshing: refreshingCustomers,
    error: customerError,
    refetch: refetchCustomers,
  } = useCustomerBreakdown(dateRange);

  // Financial health hook
  const {
    refreshing: refreshingFinancialHealth,
    refetch: refetchFinancialHealth,
  } = useFinancialHealth(ytdDateRange);

  // AMI Breakdown hook
  const { refreshing: refreshingAMIBreakdown, refetch: refetchAMIBreakdown } =
    useAMIBreakdown(ytdDateRange);

  // Cash Runway hook
  const { refreshing: refreshingCashRunway, refetch: refetchCashRunway } =
    useCashRunway(
      ytdDateRange?.start_date
        ? new Date(ytdDateRange.start_date).getFullYear()
        : new Date().getFullYear()
    );

  // Accounts Receivable hook
  const {
    refreshing: refreshingAccountsReceivable,
    refetch: refetchAccountsReceivable,
  } = useAccountsReceivable();

  // Customer metrics hook (used by ClientMix)
  const {
    refreshing: refreshingCustomerMetrics,
    refetch: refetchCustomerMetrics,
  } = useCustomerMetrics(dateRange);

  // Revenue goal state
  const [revenueGoal, setRevenueGoal] = useState(1500000);

  // Cash runway target state (in months instead of dollars)
  const [cashRunwayTarget, setCashRunwayTarget] = useState(6);

  // Section collapse state with stable initial state
  const [sectionState, setSectionState] = useState(() => ({
    revenueCollapsed: false,
    goalCollapsed: false,
    customersCollapsed: false,
    trendsCollapsed: true,
    cashCollapsed: false,
    arCollapsed: false,
    financialHealthCollapsed: false,
    amiCollapsed: false,
  }));

  // Calculate previous year total revenue from chartData
  const previousYearTotal = useMemo(() => {
    if (!chartData || chartData.length === 0) return null;

    const total = chartData.reduce((sum, month) => {
      return sum + (month.last_year_revenue || 0);
    }, 0);

    return total > 0 ? total : null;
  }, [chartData]);

  // Memoized section state updater
  const updateSectionState = useCallback((key, value) => {
    setSectionState((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Calculate dynamic revenue analysis based on current chartData
  const currentYearAnalysis = useMemo(() => {
    if (!summaryStats || !chartData) {
      return {
        totalRevenue: 0,
        averageMonthlyRevenue: 0,
        highestMonth: null,
        yoyGrowthPercentage: 0,
      };
    }

    
    // Only calculate what API doesn't provide (highest month with revenue)
    const monthsWithRevenue = chartData.filter((m) => m.revenue > 0);
    const highestMonth = monthsWithRevenue.reduce((max, month) => {
      return !max || month.revenue > max.revenue ? month : max;
    }, null);
    
    const yoyGrowth = summaryStats.yoy_growth_percentage || 
                  yoyComparison?.overall?.growth_percentage || 
                  0;
                  
    // Everything else comes directly from API summary
    return {
      totalRevenue: summaryStats.total_revenue || 0,
      averageMonthlyRevenue: summaryStats.avg_monthly_revenue || 0,
      highestMonth,
      yoyGrowthPercentage: yoyGrowth,
    };
  }, [summaryStats, chartData, yoyComparison]);

  // ============================================================================
  // EFFECTS
  // ============================================================================
  
  // Set document title and favicon - use shared utility
  useEffect(() => {
    setDocumentMeta(DOCUMENT_TITLES.FINANCE);
  }, []);

  // ============================================================================
  // ============================================================================


  // Memoized date range change handler
  const handleDateRangeChange = useCallback(
    (newDateRange) => {
      setDateRange(newDateRange);
    },
    [setDateRange]
  );

  // Memoized goal change handler
  const handleGoalChange = useCallback((newGoal) => {
    setRevenueGoal(newGoal);
  }, []);

  // Memoized cash runway target change handler (now in months)
  const handleCashRunwayTargetChange = useCallback((newTarget) => {
    setCashRunwayTarget(newTarget);
  }, []);

  // Show loading state ONLY for initial load
  if (
    (loading && !initialized) ||
    (refreshingRevenueChart && (!chartData || chartData.length === 0))
  ) {
    return <LoadingSpinner />;    
  }

  // Show error state (but only if we don't have existing data)
  if (error && (!chartData || chartData.length === 0)) {
    return <ErrorMessage message={error} onRetry={refetchRevenueChart} />;
  }

  // Check for no data - but only if initialized and not loading/refreshing
  if (
    initialized &&
    !loading &&
    !refetchRevenueChart &&
    (!chartData || !Array.isArray(chartData) || chartData.length === 0)
  ) {
    return (
      <NoDataMessage
        title="No Revenue Data"
        message="No revenue data found for the selected date range."
        onRetry={refetchRevenueChart}
      />
    );
  }

  return (
    <div className="page-wrapper">
      {/* Toast Container */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
      <div className="layout-container responsive-space">
        {/* Header - just pass the cache refresh loading state */}
        <Header
          loading={
            refreshingRevenueChart ||
            refreshingCustomers ||
            refreshingAMIBreakdown ||
            refreshingCashRunway ||
            refreshingAccountsReceivable ||
            refreshingCustomerMetrics
          }
          lastUpdated={lastUpdated}
          dateRange={dateRange}
          onDateRangeChange={handleDateRangeChange}
          yearsOnly={true}
        />

        {/* Main Content */}
        <div className="responsive-space">
          {/* Revenue Goal Section */}
          <CollapsibleSection
            title="Revenue Goal"
            subtitle="Track progress towards annual revenue target"
            collapsed={sectionState.goalCollapsed}
            setCollapsed={(value) => updateSectionState("goalCollapsed", value)}
          >
            <RevenueGoal
              currentRevenue={currentYearAnalysis.totalRevenue}
              goalAmount={revenueGoal}
              onGoalChange={handleGoalChange}
              selectedYear={
                dateRange?.start_date
                  ? parseInt(dateRange.start_date.split('-')[0], 10)
                  : new Date().getFullYear()
              }
            />
          </CollapsibleSection>

          {/* Revenue by Month Section */}
          <CollapsibleSection
            title="Revenue by Month"
            subtitle="Monthly revenue with year-over-year comparison"
            collapsed={sectionState.revenueCollapsed}
            setCollapsed={(value) =>
              updateSectionState("revenueCollapsed", value)
            }
          >
            
            <RevenueDashboard
              data={chartData}
              dateRange={dateRange}
              summaryStats={summaryStats}
              loading={loading}
              error={error}
            />

            {currentYearAnalysis.totalRevenue > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-50 rounded-lg">
                <div className="text-center p-3 sm:p-0">
                  <div className="text-lg sm:text-xl font-semibold text-gray-900">
                    $
                    {Math.round(
                      currentYearAnalysis.totalRevenue
                    ).toLocaleString()}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">Total Revenue</div>

                  {previousYearTotal && (
                    <div className="text-xs sm:text-sm text-gray-400 mt-1">
                      Last Year: $
                      {Math.round(previousYearTotal).toLocaleString()}
                    </div>
                  )}
                </div>

                <div className="text-center p-3 sm:p-0">
                  <div className="text-lg sm:text-xl font-semibold text-gray-900">
                    $
                    {Math.round(
                      currentYearAnalysis.averageMonthlyRevenue
                    ).toLocaleString()}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">
                    Avg Monthly Revenue
                  </div>
                </div>

                <div className="text-center p-3 sm:p-0">
                  <div className="text-lg sm:text-xl font-semibold text-gray-900">
                    {currentYearAnalysis.highestMonth?.month_name || "N/A"}
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">Highest Month</div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">
                    (Revenue: $
                    {Math.round(
                      currentYearAnalysis.highestMonth?.revenue || 0
                    ).toLocaleString()}
                    )
                  </div>
                </div>

                <div className="text-center p-3 sm:p-0">
                  <div
                    className={`text-lg sm:text-xl font-semibold ${
                      currentYearAnalysis.yoyGrowthPercentage > 0
                        ? "text-green-600"
                        : "text-red-600"
                    }`}
                  >
                    {currentYearAnalysis.yoyGrowthPercentage > 0 ? "+" : ""}
                    {currentYearAnalysis.yoyGrowthPercentage.toFixed(1)}%
                  </div>
                  <div className="text-xs sm:text-sm text-gray-500 mt-1">YoY Growth</div>
                </div>
              </div>
            )}
          </CollapsibleSection>

          {/* Financial Health Section */}
          <CollapsibleSection
            title="Financial Health"
            subtitle="Key financial metrics and performance indicators"
            collapsed={sectionState.financialHealthCollapsed}
            setCollapsed={(value) =>
              updateSectionState("financialHealthCollapsed", value)
            }
            rightContent={
              <div className="flex items-center gap-3">
                {formattedDateRange && (
                  <div className="text-right hidden sm:block">
                    <p className="text-xs text-gray-500 uppercase tracking-wider">YTD Period</p>
                    <p className="text-sm font-medium text-gray-900 mt-0.5">{formattedDateRange}</p>
                  </div>
                )}
              </div>
            }
          >
            {/* Mobile: Show date range above content */}
            {formattedDateRange && (
              <div className="sm:hidden mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-gray-500 uppercase tracking-wider mb-1">YTD Period</p>
                <p className="text-sm font-medium text-gray-900">{formattedDateRange}</p>
              </div>
            )}
            <FinancialHealth dateRange={ytdDateRange} />
            <AMIBreakdown dateRange={ytdDateRange} />
          </CollapsibleSection>

          {/* Cash Runway Section */}
          <CollapsibleSection
            title="Cash"
            subtitle="Track months of operating expenses with current cash"
            collapsed={sectionState.cashCollapsed}
            setCollapsed={(value) => updateSectionState("cashCollapsed", value)}
          >
            <CashRunway
              selectedYear={
                dateRange?.start_date
                  ? parseInt(dateRange.start_date.split('-')[0], 10)
                  : new Date().getFullYear()
              }
              goalMonths={cashRunwayTarget}
              onGoalChange={handleCashRunwayTargetChange}
            />
          </CollapsibleSection>

          {/* Accounts Receivable Section */}
          <CollapsibleSection
            title="Accounts Receivable"
            subtitle="Track outstanding invoices and overdue balances"
            collapsed={sectionState.arCollapsed}
            setCollapsed={(value) => updateSectionState("arCollapsed", value)}
          >
            <AccountsReceivable />
          </CollapsibleSection>

          {/* Customer Revenue Breakdown Section */}
          <CollapsibleSection
            title="Client Mix"
            subtitle="Revenue distribution by customer"
            collapsed={sectionState.customersCollapsed}
            setCollapsed={(value) =>
              updateSectionState("customersCollapsed", value)
            }
          >
            <ClientMix
              customerData={customerBreakdown}
              totalCustomers={totalCustomers}
              retainerCustomers={totalRetainers}
              projectCustomers={totalProjects}
              dateRange={dateRange}
              loading={loadingCustomers}
              error={customerError}
              onRefresh={refetchCustomers}
              selectedYear={
                dateRange?.start_date
                  ? parseInt(dateRange.start_date.split('-')[0], 10)
                  : new Date().getFullYear()
              }
            />
          </CollapsibleSection>
        </div>
      </div>
    </div>
  );
};

// Memoize the entire component
export default memo(FinanceDashboard);